<template>
  <div class="step-card">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />

          <circle
            cx="12"
            cy="9"
            r="2.5"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Местоположение
        </span>

        <h3 class="step-title">
          Где находится компания?
        </h3>
      </div>
    </div>

    <p class="description">
      Укажите адрес офиса компании. Если ваша команда полностью работает
      удалённо, включите режим «Только ремоут».
    </p>

    <!-- Remote -->
    <label
      class="remote-card"
      :class="{ active: model.isRemote }"
    >
      <div class="remote-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect
            x="3.5"
            y="5"
            width="17"
            height="11"
            rx="2"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <path
            d="M8 20h8M12 16v4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="m9.2 10.5 1.8 1.8 3.8-4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="remote-content">
        <span class="remote-title">
          Только ремоут
        </span>

        <span class="remote-description">
          У компании нет обязательного офиса для сотрудников
        </span>
      </div>

      <input
        v-model="model.isRemote"
        type="checkbox"
        class="toggle-input"
        @change="handleRemote"
      />

      <span class="toggle-slider">
        <span class="toggle-thumb"></span>
      </span>
    </label>

    <!-- Контент адреса -->
    <Transition name="address-content" mode="out-in">
      <div
        v-if="!model.isRemote"
        key="office"
        class="office-content"
      >
        <div class="field-heading">
          <label
            class="field-label"
            for="company-address"
          >
            Адрес офиса
          </label>

          <Transition name="status" mode="out-in">
            <span
              v-if="isSearching"
              key="searching"
              class="field-status searching"
            >
              <span class="small-loader"></span>
              Ищем адрес
            </span>

            <span
              v-else-if="addressConfirmed"
              key="confirmed"
              class="field-status confirmed"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  fill="currentColor"
                />

                <path
                  d="m6.4 10.1 2.2 2.2 5-5"
                  stroke="white"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              Адрес найден
            </span>
          </Transition>
        </div>

        <div
          class="input-wrapper"
          :class="{
            'is-focused': isAddressFocused,
            'is-loading': isSearching,
            'is-valid': addressConfirmed,
            'is-error': showAddressError
          }"
        >
          <div class="input-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
              />

              <circle
                cx="12"
                cy="9"
                r="2.5"
                stroke="currentColor"
                stroke-width="1.8"
              />
            </svg>
          </div>

          <input
            id="company-address"
            v-model="model.street"
            type="text"
            class="text-input"
            placeholder="Например: Москва, ул. Тверская, 12"
            autocomplete="street-address"
            @input="handleAddressInput"
            @focus="isAddressFocused = true"
            @blur="handleAddressBlur"
          />

          <Transition name="check">
            <div
              v-if="addressConfirmed && !isSearching"
              class="success-icon"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m5.5 10.2 2.8 2.8 6.2-6.2"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </Transition>
        </div>

        <Transition name="message" mode="out-in">
          <div
            v-if="showAddressError"
            key="error"
            class="field-message error-message"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <path
                d="M10 6.3v4.4M10 13.6v.1"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

            {{ addressError }}
          </div>

          <div
            v-else
            key="hint"
            class="field-message hint-message"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                stroke-width="1.5"
              />

              <path
                d="M10 9v5M10 6.2v.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

            Укажите город, улицу и номер дома
          </div>
        </Transition>

        <!-- Карта -->
        <div class="map-section">
          <div class="map-heading">
            <div>
              <span class="map-title">
                Местоположение на карте
              </span>

              <span class="map-subtitle">
                Проверьте, правильно ли определён адрес
              </span>
            </div>

            <span
              v-if="addressConfirmed"
              class="coordinates-badge"
            >
              {{ formattedCoordinates }}
            </span>
          </div>

          <div
            class="map-wrapper"
            :class="{ loading: isMapLoading }"
          >
            <iframe
              :key="mapUrl"
              class="map-frame"
              :src="mapUrl"
              title="Местоположение компании"
              loading="lazy"
              allowfullscreen
              referrerpolicy="no-referrer-when-downgrade"
              @load="handleMapLoaded"
            ></iframe>

            <Transition name="map-loader">
              <div
                v-if="isMapLoading"
                class="map-loading-overlay"
              >
                <div class="map-loader">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>

                <span>Загружаем карту</span>
              </div>
            </Transition>

            <div class="map-gradient"></div>

            <div
              v-if="addressConfirmed"
              class="map-address-card"
            >
              <div class="map-pin">
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M10 18s5.5-4.2 5.5-9.5a5.5 5.5 0 1 0-11 0C4.5 13.8 10 18 10 18Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <circle
                    cx="10"
                    cy="8.5"
                    r="1.8"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
              </div>

              <div class="map-address-content">
                <span>Офис компании</span>
                <strong>{{ resolvedAddress }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Remote-состояние -->
      <div
        v-else
        key="remote"
        class="remote-state"
      >
        <div class="remote-illustration">
          <div class="remote-orbit orbit-one"></div>
          <div class="remote-orbit orbit-two"></div>

          <div class="remote-main-icon">
            <svg
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="4"
                y="6"
                width="24"
                height="16"
                rx="3"
                stroke="currentColor"
                stroke-width="2"
              />

              <path
                d="M11 27h10M16 22v5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />

              <path
                d="m12 14 2.5 2.5L20.5 11"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>

        <h4>Работа из любой точки мира</h4>

        <p>
          Адрес офиса указывать не нужно. В карточке компании будет
          отмечено, что команда работает полностью удалённо.
        </p>

        <div class="remote-benefits">
          <span>
            <svg viewBox="0 0 20 20" fill="none">
              <path
                d="m5.5 10 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Без привязки к офису
          </span>

          <span>
            <svg viewBox="0 0 20 20" fill="none">
              <path
                d="m5.5 10 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Доступно кандидатам из других городов
          </span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  ref,
  watch
} from "vue";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

/*
  Добавь ключ в .env:

  VITE_GOOGLE_MAPS_API_KEY=твой_ключ

  После изменения .env перезапусти dev-сервер.
*/
const GOOGLE_MAPS_API_KEY =
  "AIzaSyBSEN-wo__FMd5MeQUIkQYGFPuqZeHgVNY" || "";

const DEFAULT_MAP_QUERY = "Москва";

const isAddressFocused = ref(false);
const isAddressTouched = ref(false);
const isSearching = ref(false);
const isMapLoading = ref(true);

const addressConfirmed = ref(false);
const addressError = ref("");
const resolvedAddress = ref("");

let debounceTimer = null;
let geocodeController = null;

initializeModel();

const hasAddress = computed(() =>
  (model.value.street || "").trim().length >= 3
);

const hasCoordinates = computed(() => {
  const latitude = model.value.location?.latitude;
  const longitude = model.value.location?.longitude;

  return (
    typeof latitude === "number" &&
    typeof longitude === "number"
  );
});

const isStepValid = computed(() =>
  model.value.isRemote ||
  (hasAddress.value && addressConfirmed.value && hasCoordinates.value)
);

const showAddressError = computed(() =>
  !model.value.isRemote &&
  isAddressTouched.value &&
  !isSearching.value &&
  Boolean(addressError.value)
);

const formattedCoordinates = computed(() => {
  if (!hasCoordinates.value) {
    return "";
  }

  const latitude =
    model.value.location.latitude.toFixed(4);

  const longitude =
    model.value.location.longitude.toFixed(4);

  return `${latitude}, ${longitude}`;
});

const mapQuery = computed(() => {
  if (hasCoordinates.value) {
    return [
      model.value.location.latitude,
      model.value.location.longitude
    ].join(",");
  }

  return (
    resolvedAddress.value ||
    model.value.street?.trim() ||
    DEFAULT_MAP_QUERY
  );
});

const mapUrl = computed(() => {
  if (!GOOGLE_MAPS_API_KEY) {
    return "";
  }

  return (
    "https://www.google.com/maps/embed/v1/place" +
    `?key=${GOOGLE_MAPS_API_KEY}` +
    `&q=${encodeURIComponent(mapQuery.value)}` +
    "&zoom=15"
  );
});

function initializeModel() {
  if (typeof model.value.isRemote !== "boolean") {
    model.value.isRemote = false;
  }

  if (typeof model.value.street !== "string") {
    model.value.street = "";
  }

  if (
    !model.value.location ||
    typeof model.value.location !== "object"
  ) {
    model.value.location = {
      latitude: null,
      longitude: null
    };
  }
}

function clearCoordinates() {
  model.value.location.latitude = null;
  model.value.location.longitude = null;

  addressConfirmed.value = false;
  resolvedAddress.value = "";
}

function handleAddressInput() {
  isAddressTouched.value = true;
  addressError.value = "";

  clearTimeout(debounceTimer);

  if (!hasAddress.value) {
    geocodeController?.abort();
    clearCoordinates();

    if ((model.value.street || "").trim().length > 0) {
      addressError.value =
        "Введите более подробный адрес";
    }

    emitValidationState();
    return;
  }

  clearCoordinates();

  debounceTimer = window.setTimeout(() => {
    updateAddress();
  }, 650);
}

function handleAddressBlur() {
  isAddressFocused.value = false;
  isAddressTouched.value = true;

  if (!hasAddress.value && model.value.street?.trim()) {
    addressError.value =
      "Введите более подробный адрес";
  }

  emitValidationState();
}

async function updateAddress() {
  if (model.value.isRemote || !hasAddress.value) {
    clearCoordinates();
    emitValidationState();
    return;
  }

  if (!GOOGLE_MAPS_API_KEY) {
    addressError.value =
      "Не настроен ключ Google Maps";
    emitValidationState();
    return;
  }

  geocodeController?.abort();
  geocodeController = new AbortController();

  isSearching.value = true;
  addressError.value = "";

  const address = model.value.street.trim();

  const url =
    "https://maps.googleapis.com/maps/api/geocode/json" +
    `?address=${encodeURIComponent(address)}` +
    `&key=${GOOGLE_MAPS_API_KEY}` +
    "&language=ru";

  try {
    const response = await fetch(url, {
      signal: geocodeController.signal
    });

    if (!response.ok) {
      throw new Error(
        `Google Maps HTTP error: ${response.status}`
      );
    }

    const data = await response.json();

    if (
      data.status === "OK" &&
      Array.isArray(data.results) &&
      data.results.length > 0
    ) {
      const result = data.results[0];
      const location = result.geometry?.location;

      if (
        typeof location?.lat !== "number" ||
        typeof location?.lng !== "number"
      ) {
        throw new Error("Coordinates not found");
      }

      model.value.location.latitude = location.lat;
      model.value.location.longitude = location.lng;

      resolvedAddress.value =
        result.formatted_address || address;

      addressConfirmed.value = true;
      addressError.value = "";

      isMapLoading.value = true;
    } else if (data.status === "ZERO_RESULTS") {
      clearCoordinates();

      addressError.value =
        "Не удалось найти такой адрес";
    } else {
      clearCoordinates();

      addressError.value =
        "Не удалось проверить адрес. Попробуйте ещё раз";

      console.error(
        "Google Geocoding error:",
        data.status,
        data.error_message
      );
    }
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    clearCoordinates();

    addressError.value =
      "Ошибка при поиске адреса. Проверьте подключение";

    console.error(
      "Ошибка получения координат:",
      error
    );
  } finally {
    isSearching.value = false;
    emitValidationState();
  }
}

function handleRemote() {
  clearTimeout(debounceTimer);
  geocodeController?.abort();

  addressError.value = "";
  isSearching.value = false;

  if (model.value.isRemote) {
    model.value.street = "";
    clearCoordinates();
  }

  emitValidationState();
}

function handleMapLoaded() {
  isMapLoading.value = false;
}

function emitValidationState() {
  if (isStepValid.value) {
    emit("valid");
  } else {
    emit("not-valid");
  }
}

watch(
  () => model.value.isRemote,
  () => {
    emitValidationState();
  },
  { immediate: true }
);

watch(
  mapUrl,
  () => {
    if (mapUrl.value) {
      isMapLoading.value = true;
    }
  }
);

onBeforeUnmount(() => {
  clearTimeout(debounceTimer);
  geocodeController?.abort();
});
</script>

<style scoped>

* {
  box-sizing: border-box;
}

.step-card {
  width: 100%;
  padding: 28px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  animation: card-appearance 0.45s ease both;
}

/* Header */

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 18px;
}

.step-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.25;
}

.description {
  max-width: 700px;
  margin: 0 0 23px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.65;
}

/* Remote */

.remote-card {
  width: 100%;
  max-width: 660px;

  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 24px;
  padding: 15px 16px;

  cursor: pointer;

  background: #f7f8fa;

  border: 1px solid #e4e7ec;
  border-radius: 16px;

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.remote-card:hover {
  background: #fafbfc;
  border-color: #d8dde5;

  transform: translateY(-1px);
}

.remote-card.active {
  background:
    linear-gradient(
      135deg,
      rgba(51, 139, 255, 0.09),
      rgba(95, 92, 238, 0.065)
    );

  border-color: rgba(60, 132, 242, 0.28);

  box-shadow:
    0 8px 22px rgba(48, 117, 224, 0.075);
}

.remote-icon {
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #6e7889;
  background: #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 12px;

  transition:
    color 0.22s ease,
    background-color 0.22s ease,
    transform 0.22s ease;
}

.remote-icon svg {
  width: 22px;
  height: 22px;
}

.remote-card.active .remote-icon {
  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #319aff,
      #4a6ff0
    );

  border-color: transparent;

  box-shadow:
    0 6px 14px rgba(48, 126, 246, 0.2);

  transform: rotate(-2deg) scale(1.03);
}

.remote-content {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;

  gap: 3px;
}

.remote-title {
  color: #303641;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
  line-height: 1.3;
}

.remote-description {
  color: #8b93a1;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.4;
}

.toggle-input {
  position: absolute;

  width: 1px;
  height: 1px;

  opacity: 0;
  pointer-events: none;
}

.toggle-slider {
  position: relative;

  width: 48px;
  height: 28px;

  flex-shrink: 0;

  background: #d5d9df;
  border-radius: 999px;

  box-shadow:
    inset 0 1px 3px rgba(15, 23, 42, 0.12);

  transition:
    background-color 0.25s ease,
    box-shadow 0.25s ease;
}

.toggle-thumb {
  position: absolute;
  top: 4px;
  left: 4px;

  width: 20px;
  height: 20px;

  background: #ffffff;
  border-radius: 50%;

  box-shadow:
    0 2px 6px rgba(15, 23, 42, 0.2);

  transition:
    transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease;
}

.toggle-input:checked + .toggle-slider {
  background:
    linear-gradient(
      135deg,
      #2d97ff,
      #526cec
    );

  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.18),
    0 4px 10px rgba(48, 126, 246, 0.22);
}

.toggle-input:checked + .toggle-slider .toggle-thumb {
  transform: translateX(20px);
}

.toggle-input:focus-visible + .toggle-slider {
  outline: 3px solid rgba(55, 135, 246, 0.22);
  outline-offset: 3px;
}

/* Office */

.office-content {
  width: 100%;
  max-width: 660px;
}

.field-heading {
  min-height: 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.field-label {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

.field-status {
  display: flex;
  align-items: center;
  gap: 6px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
}

.field-status.confirmed {
  color: #27a268;
}

.field-status.confirmed svg {
  width: 15px;
  height: 15px;
}

.field-status.searching {
  color: #5483cc;
}

.small-loader {
  width: 13px;
  height: 13px;

  border: 2px solid rgba(67, 136, 245, 0.18);
  border-top-color: #4388f5;
  border-radius: 50%;

  animation: spin 0.7s linear infinite;
}

.input-wrapper {
  position: relative;

  width: 100%;
  min-height: 54px;

  display: flex;
  align-items: center;

  overflow: hidden;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.025);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.input-wrapper:hover {
  background: #fafbfc;
  border-color: #d4d9e1;
}

.input-wrapper.is-focused {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 24px rgba(31, 96, 205, 0.08);

  transform: translateY(-1px);
}

.input-wrapper.is-valid:not(.is-focused) {
  border-color: rgba(39, 162, 104, 0.4);
}

.input-wrapper.is-error {
  background: rgba(255, 246, 246, 0.9);
  border-color: #e15b5b;

  box-shadow:
    0 0 0 4px rgba(225, 91, 91, 0.09);
}

.input-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 16px;

  color: #939cab;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.input-icon svg {
  width: 20px;
  height: 20px;
}

.input-wrapper.is-focused .input-icon {
  color: #4388f5;
  transform: scale(1.05);
}

.text-input {
  min-width: 0;
  width: 100%;

  padding: 16px 12px;

  color: #242933;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;
}

.text-input::placeholder {
  color: #a7aeba;
}

.success-icon {
  width: 26px;
  height: 26px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 14px;

  color: #28a46b;
  background: rgba(40, 164, 107, 0.1);

  border-radius: 50%;
}

.success-icon svg {
  width: 16px;
  height: 16px;
}

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin-top: 9px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.45;
}

.field-message svg {
  width: 15px;
  height: 15px;

  flex-shrink: 0;
  margin-top: 1px;
}

.hint-message {
  color: #8a93a2;
}

.error-message {
  color: #d84e4e;
  font-weight: 600;
}

/* Map */

.map-section {
  margin-top: 24px;
}

.map-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  margin-bottom: 10px;
}

.map-heading > div {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 3px;
}

.map-title {
  color: #343a45;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

.map-subtitle {
  color: #9299a5;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.coordinates-badge {
  flex-shrink: 0;

  padding: 6px 9px;

  color: #5f779b;
  background: #f2f5f9;

  border: 1px solid #e3e8ef;
  border-radius: 8px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.map-wrapper {
  position: relative;

  width: 100%;
  height: 300px;

  overflow: hidden;

  background: #e8ecf1;

  border: 1px solid #e2e5ea;
  border-radius: 18px;

  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.07);

  transform: translateZ(0);
}

.map-frame {
  width: 100%;
  height: 100%;

  border: none;

  transition:
    opacity 0.35s ease,
    transform 0.4s ease;
}

.map-wrapper.loading .map-frame {
  opacity: 0.35;
  transform: scale(1.015);
}

.map-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 12px;

  color: #768092;
  background: rgba(241, 244, 248, 0.78);
  backdrop-filter: blur(5px);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.map-loader {
  display: flex;
  gap: 5px;
}

.map-loader span {
  width: 7px;
  height: 7px;

  background: #4388f5;
  border-radius: 50%;

  animation: map-bounce 1s ease-in-out infinite;
}

.map-loader span:nth-child(2) {
  animation-delay: 0.13s;
}

.map-loader span:nth-child(3) {
  animation-delay: 0.26s;
}

.map-gradient {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;

  height: 105px;

  pointer-events: none;

  background:
    linear-gradient(
      to top,
      rgba(20, 29, 44, 0.24),
      transparent
    );
}

.map-address-card {
  position: absolute;
  right: 13px;
  bottom: 13px;
  left: 13px;
  z-index: 2;

  display: flex;
  align-items: center;
  gap: 10px;

  padding: 11px 12px;

  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px);

  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 13px;

  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.14);

  animation: address-card-appearance 0.35s ease both;
}

.map-pin {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #319aff,
      #4e6eef
    );

  border-radius: 10px;
}

.map-pin svg {
  width: 19px;
  height: 19px;
}

.map-address-content {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.map-address-content span {
  color: #8b93a1;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.map-address-content strong {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: #303641;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

/* Remote state */

.remote-state {
  width: 100%;
  max-width: 660px;
  min-height: 330px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  padding: 34px 24px;

  text-align: center;

  background:
    radial-gradient(
      circle at 50% 35%,
      rgba(48, 136, 255, 0.1),
      transparent 180px
    ),
    #f8f9fb;

  border: 1px dashed rgba(67, 136, 245, 0.25);
  border-radius: 20px;
}

.remote-illustration {
  position: relative;

  width: 112px;
  height: 112px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 20px;
}

.remote-main-icon {
  position: relative;
  z-index: 2;

  width: 68px;
  height: 68px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #32a0ff,
      #4e6def
    );

  border-radius: 21px;

  box-shadow:
    0 14px 30px rgba(48, 126, 246, 0.25);

  animation: remote-float 3s ease-in-out infinite;
}

.remote-main-icon svg {
  width: 34px;
  height: 34px;
}

.remote-orbit {
  position: absolute;

  border: 1px solid rgba(67, 136, 245, 0.15);
  border-radius: 50%;

  animation: orbit-pulse 3s ease-in-out infinite;
}

.orbit-one {
  width: 90px;
  height: 90px;
}

.orbit-two {
  width: 112px;
  height: 112px;

  animation-delay: 0.5s;
}

.remote-state h4 {
  margin: 0 0 9px;

  color: #29303b;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 17px;
}

.remote-state > p {
  max-width: 460px;
  margin: 0;

  color: #818a99;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.6;
}

.remote-benefits {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  gap: 8px;

  margin-top: 20px;
}

.remote-benefits span {
  display: flex;
  align-items: center;
  gap: 5px;

  padding: 7px 10px;

  color: #60718d;
  background: rgba(255, 255, 255, 0.82);

  border: 1px solid rgba(68, 116, 187, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.remote-benefits svg {
  width: 13px;
  height: 13px;

  color: #359660;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes map-bounce {
  0%,
  60%,
  100% {
    opacity: 0.45;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-5px);
  }
}

@keyframes address-card-appearance {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes remote-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

@keyframes orbit-pulse {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(0.96);
  }

  50% {
    opacity: 1;
    transform: scale(1.04);
  }
}

/* Vue transitions */

.address-content-enter-active,
.address-content-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.28s ease;
}

.address-content-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.address-content-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.status-enter-active,
.status-leave-active,
.check-enter-active,
.check-leave-active,
.message-enter-active,
.message-leave-active,
.map-loader-enter-active,
.map-loader-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: translateX(5px);
}

.check-enter-from,
.check-leave-to {
  opacity: 0;
  transform: scale(0.65);
}

.message-enter-from {
  opacity: 0;
  transform: translateY(-3px);
}

.message-leave-to {
  opacity: 0;
  transform: translateY(3px);
}

.map-loader-enter-from,
.map-loader-leave-to {
  opacity: 0;
}

/* Dark mode */

html[data-theme="dark"] .step-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1e;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .step-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .remote-card {
  background-color: #26262a;
  border-color: #38383e;
}

html[data-theme="dark"] .remote-card:hover {
  background-color: #29292e;
  border-color: #46464d;
}

html[data-theme="dark"] .remote-card.active {
  background:
    linear-gradient(
      135deg,
      rgba(48, 132, 255, 0.13),
      rgba(94, 91, 238, 0.09)
    );

  border-color: rgba(69, 140, 248, 0.3);
}

html[data-theme="dark"] .remote-icon {
  color: #949aa5;
  background-color: #303035;
  border-color: #414147;
}

html[data-theme="dark"] .remote-card.active .remote-icon {
  color: #ffffff;
  border-color: transparent;
}

html[data-theme="dark"] .remote-title {
  color: #eceef2;
}

html[data-theme="dark"] .remote-description {
  color: #858b96;
}

html[data-theme="dark"] .toggle-slider {
  background-color: #48484f;
}

html[data-theme="dark"] .field-label,
html[data-theme="dark"] .map-title {
  color: #d8dae0;
}

html[data-theme="dark"] .field-status.searching {
  color: #75a9f8;
}

html[data-theme="dark"] .field-status.confirmed {
  color: #56cb8e;
}

html[data-theme="dark"] .small-loader {
  border-color: rgba(103, 162, 255, 0.2);
  border-top-color: #67a2ff;
}

html[data-theme="dark"] .input-wrapper {
  background-color: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .input-wrapper:hover {
  background-color: #2c2d31;
  border-color: #4a4c53;
}

html[data-theme="dark"] .input-wrapper.is-focused {
  background-color: #2d2f34;
  border-color: #5989eb;

  box-shadow:
    0 0 0 4px rgba(70, 126, 229, 0.15),
    0 9px 24px rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .input-wrapper.is-valid:not(.is-focused) {
  border-color: rgba(61, 192, 126, 0.62);
}

html[data-theme="dark"] .input-wrapper.is-error {
  background-color: rgba(91, 37, 40, 0.22);
  border-color: #d85b60;

  box-shadow:
    0 0 0 4px rgba(216, 91, 96, 0.12);
}

html[data-theme="dark"] .input-icon {
  color: #7f848e;
}

html[data-theme="dark"] .input-wrapper.is-focused .input-icon {
  color: #67a2ff;
}

html[data-theme="dark"] .input-wrapper.is-error .input-icon {
  color: #ff858b;
}

html[data-theme="dark"] .text-input {
  color: #f2f2f4;
  caret-color: #67a2ff;
}

html[data-theme="dark"] .text-input::placeholder {
  color: #70747c;
}

html[data-theme="dark"] .success-icon {
  color: #51c989;
  background-color: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .hint-message {
  color: #7f838b;
}

html[data-theme="dark"] .error-message {
  color: #ff8b94;
}

html[data-theme="dark"] .map-subtitle {
  color: #858b96;
}

html[data-theme="dark"] .coordinates-badge {
  color: #98a8c0;
  background-color: #292b30;
  border-color: #3b3e45;
}

html[data-theme="dark"] .map-wrapper {
  background-color: #27282c;
  border-color: #3a3b41;

  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.24);
}

html[data-theme="dark"] .map-loading-overlay {
  color: #a0a6b0;
  background: rgba(35, 36, 41, 0.84);
}

html[data-theme="dark"] .map-address-card {
  background: rgba(37, 38, 43, 0.94);
  border-color: rgba(255, 255, 255, 0.08);

  box-shadow:
    0 9px 28px rgba(0, 0, 0, 0.32);
}

html[data-theme="dark"] .map-address-content span {
  color: #8b93a1;
}

html[data-theme="dark"] .map-address-content strong {
  color: #f0f1f3;
}

html[data-theme="dark"] .remote-state {
  background:
    radial-gradient(
      circle at 50% 35%,
      rgba(48, 136, 255, 0.11),
      transparent 180px
    ),
    #242428;

  border-color: rgba(67, 136, 245, 0.22);
}

html[data-theme="dark"] .remote-state h4 {
  color: #eff0f2;
}

html[data-theme="dark"] .remote-state > p {
  color: #9096a0;
}

html[data-theme="dark"] .remote-orbit {
  border-color: rgba(103, 162, 255, 0.18);
}

html[data-theme="dark"] .remote-benefits span {
  color: #a6afbd;
  background-color: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.07);
}

html[data-theme="dark"] .remote-benefits svg {
  color: #51c989;
}

/* Compact laptop mode
   1366x768 / 1366x788 / небольшие 13"
*/

@media (min-width: 721px) and (max-height: 820px) {
  .step-card {
    padding: 16px 22px;
  }

  .step-header {
    gap: 10px;
    margin-bottom: 9px;
  }

  .step-icon {
    width: 38px;
    height: 38px;
    border-radius: 11px;
  }

  .step-icon svg {
    width: 20px;
    height: 20px;
  }

  .step-heading {
    gap: 1px;
  }

  .step-label {
    font-size: 8px;
  }

  .step-title {
    font-size: 16px;
    line-height: 1.15;
  }

  .description {
    max-width: 660px;

    margin-bottom: 11px;

    font-size: 11px;
    line-height: 1.35;
  }

  .remote-card {
    gap: 10px;

    margin-bottom: 12px;
    padding: 9px 12px;

    border-radius: 12px;
  }

  .remote-icon {
    width: 34px;
    height: 34px;

    border-radius: 9px;
  }

  .remote-icon svg {
    width: 19px;
    height: 19px;
  }

  .remote-content {
    gap: 1px;
  }

  .remote-title {
    font-size: 11px;
    line-height: 1.2;
  }

  .remote-description {
    font-size: 9px;
    line-height: 1.25;
  }

  .toggle-slider {
    width: 42px;
    height: 24px;
  }

  .toggle-thumb {
    top: 3px;
    left: 3px;

    width: 18px;
    height: 18px;
  }

  .toggle-input:checked + .toggle-slider .toggle-thumb {
    transform: translateX(18px);
  }

  .field-heading {
    min-height: 16px;
    margin-bottom: 5px;
  }

  .field-label {
    font-size: 10px;
  }

  .field-status {
    gap: 4px;
    font-size: 9px;
  }

  .field-status.confirmed svg {
    width: 13px;
    height: 13px;
  }

  .small-loader {
    width: 11px;
    height: 11px;
    border-width: 1.5px;
  }

  .input-wrapper {
    min-height: 42px;
    border-radius: 11px;
  }

  .input-wrapper.is-focused {
    box-shadow:
      0 0 0 3px rgba(67, 136, 245, 0.1),
      0 5px 15px rgba(31, 96, 205, 0.06);
  }

  .input-icon {
    width: 17px;
    height: 17px;
    margin-left: 12px;
  }

  .input-icon svg {
    width: 17px;
    height: 17px;
  }

  .text-input {
    padding: 11px 10px;
    font-size: 12px;
  }

  .success-icon {
    width: 23px;
    height: 23px;
    margin-right: 10px;
  }

  .success-icon svg {
    width: 14px;
    height: 14px;
  }

  .field-message {
    min-height: 15px;
    gap: 5px;
    margin-top: 4px;

    font-size: 9px;
    line-height: 1.25;
  }

  .field-message svg {
    width: 12px;
    height: 12px;
    margin-top: 0;
  }

  .map-section {
    margin-top: 10px;
  }

  .map-heading {
    gap: 10px;
    margin-bottom: 6px;
  }

  .map-heading > div {
    gap: 1px;
  }

  .map-title {
    font-size: 10px;
  }

  .map-subtitle {
    font-size: 8px;
  }

  .coordinates-badge {
    padding: 4px 7px;
    border-radius: 6px;
    font-size: 8px;
  }

  .map-wrapper {
    height: 185px;
    border-radius: 13px;

    box-shadow:
      0 5px 16px rgba(15, 23, 42, 0.06);
  }

  .map-gradient {
    height: 70px;
  }

  .map-address-card {
    right: 8px;
    bottom: 8px;
    left: 8px;

    gap: 8px;
    padding: 7px 9px;

    border-radius: 10px;
  }

  .map-pin {
    width: 27px;
    height: 27px;
    border-radius: 8px;
  }

  .map-pin svg {
    width: 16px;
    height: 16px;
  }

  .map-address-content {
    gap: 1px;
  }

  .map-address-content span {
    font-size: 7px;
  }

  .map-address-content strong {
    font-size: 9px;
  }

  .map-loading-overlay {
    gap: 8px;
    font-size: 9px;
  }

  .map-loader {
    gap: 4px;
  }

  .map-loader span {
    width: 6px;
    height: 6px;
  }

  .remote-state {
    min-height: 235px;

    padding: 18px 20px;

    border-radius: 15px;
  }

  .remote-illustration {
    width: 76px;
    height: 76px;

    margin-bottom: 10px;
  }

  .remote-main-icon {
    width: 48px;
    height: 48px;

    border-radius: 15px;

    box-shadow:
      0 8px 20px rgba(48, 126, 246, 0.2);
  }

  .remote-main-icon svg {
    width: 25px;
    height: 25px;
  }

  .orbit-one {
    width: 62px;
    height: 62px;
  }

  .orbit-two {
    width: 76px;
    height: 76px;
  }

  .remote-state h4 {
    margin-bottom: 5px;
    font-size: 14px;
  }

  .remote-state > p {
    max-width: 500px;

    font-size: 10px;
    line-height: 1.4;
  }

  .remote-benefits {
    gap: 6px;
    margin-top: 10px;
  }

  .remote-benefits span {
    gap: 4px;

    padding: 5px 8px;

    font-size: 8px;
  }

  .remote-benefits svg {
    width: 11px;
    height: 11px;
  }
}

/* 1366x768 */

@media (min-width: 721px) and (max-height: 770px) {
  .step-card {
    padding-top: 13px;
    padding-bottom: 13px;
  }

  .step-header {
    margin-bottom: 7px;
  }

  .description {
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .remote-card {
    margin-bottom: 9px;
    padding-top: 7px;
    padding-bottom: 7px;
  }

  .remote-icon {
    width: 31px;
    height: 31px;
  }

  .input-wrapper {
    min-height: 39px;
  }

  .text-input {
    padding-top: 9px;
    padding-bottom: 9px;
  }

  .field-message {
    margin-top: 3px;
  }

  .map-section {
    margin-top: 7px;
  }

  .map-heading {
    margin-bottom: 4px;
  }

  .map-wrapper {
    height: 165px;
  }

  .remote-state {
    min-height: 210px;
    padding: 14px 18px;
  }

  .remote-illustration {
    width: 66px;
    height: 66px;
    margin-bottom: 7px;
  }

  .remote-main-icon {
    width: 43px;
    height: 43px;
  }

  .orbit-one {
    width: 55px;
    height: 55px;
  }

  .orbit-two {
    width: 66px;
    height: 66px;
  }

  .remote-benefits {
    margin-top: 7px;
  }
}

/* Mobile */

@media (max-width: 720px) {
  .step-card {
    padding: 21px 18px;
    border-radius: 20px;
  }

  .step-icon {
    width: 40px;
    height: 40px;
    border-radius: 13px;
  }

  .step-title {
    font-size: 17px;
  }

  .description {
    font-size: 13px;
  }

  .remote-card {
    padding: 13px;
  }

  .remote-description {
    padding-right: 4px;
  }

  .map-wrapper {
    height: 250px;
    border-radius: 16px;
  }

  .map-heading {
    align-items: flex-start;
  }

  .coordinates-badge {
    display: none;
  }
}

@media (max-width: 480px) {
  .remote-icon {
    display: none;
  }

  .remote-card {
    gap: 10px;
  }

  .toggle-slider {
    width: 44px;
    height: 26px;
  }

  .toggle-thumb {
    width: 18px;
    height: 18px;
  }

  .toggle-input:checked + .toggle-slider .toggle-thumb {
    transform: translateX(18px);
  }

  .map-wrapper {
    height: 220px;
  }

  .map-address-card {
    right: 9px;
    bottom: 9px;
    left: 9px;
  }

  .remote-state {
    min-height: 310px;
    padding: 28px 16px;
  }

  .remote-benefits {
    align-items: stretch;
    flex-direction: column;
  }

  .remote-benefits span {
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-card,
  .remote-main-icon,
  .remote-orbit,
  .map-loader span,
  .small-loader {
    animation: none;
  }

  .remote-card,
  .remote-icon,
  .toggle-slider,
  .toggle-thumb,
  .input-wrapper,
  .input-icon,
  .map-frame,
  .address-content-enter-active,
  .address-content-leave-active,
  .status-enter-active,
  .status-leave-active,
  .check-enter-active,
  .check-leave-active,
  .message-enter-active,
  .message-leave-active,
  .map-loader-enter-active,
  .map-loader-leave-active {
    transition: none;
  }
}

</style>